import React from 'react';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useMicrosoftClarity } from '@/hooks/useMicrosoftClarity';
import { generateFilenameStrict } from '@/utils/filename';
import { isDevelopment, devLog } from '@/utils/environment';

interface VideoFormat {
  url: string;
  label: string;
  resolution?: string;
  ext: string;
  filesize?: number;
  tbr?: number;
  abr?: number;
  vbr?: number;
  is_audio_only: boolean;
  has_audio: boolean;
}

interface VideoInfo {
  title: string;
  uploader?: string;
  description?: string;
  thumbnail_url?: string;
  duration?: number;
  formats: VideoFormat[];
  is_podcast?: boolean;
  series?: string;
  episode_number?: number;
  season_number?: number;
  platform?: string;
}

interface VideoInfoDisplayProps {
  videoInfo: VideoInfo;
  sourceUrl: string;
  onFormatSelect: (format: VideoFormat, filename: string) => void;
  className?: string;
  onClear?: () => void;
}

const VideoInfoDisplay: React.FC<VideoInfoDisplayProps> = ({
  videoInfo,
  sourceUrl,
  onFormatSelect,
  className = '',
  onClear
}) => {
  const config = useDomainConfig();
  const { trackVideoDownload } = useGoogleAnalytics();
  const { trackVideoDownload: trackClarityVideoDownload } = useMicrosoftClarity();

  // Auto-scroll to video info when component mounts
  React.useEffect(() => {
    // Add a delay to ensure the component is fully rendered
    const timer = setTimeout(() => {
      const containerElement = document.getElementById('video-info-display');

      // Debug logging for development
      if (isDevelopment()) {
        devLog('🔄 Auto-scroll triggered');
        devLog('📍 Container element found:', !!containerElement);
        if (containerElement) {
          devLog('📏 Container position:', containerElement.getBoundingClientRect());
        }
      }

      if (containerElement) {
        // Check if there's a thumbnail image to scroll to specifically
        const thumbnailImg = containerElement.querySelector('img');

        if (thumbnailImg && videoInfo.thumbnail_url) {
          // Scroll to show the thumbnail at the top with proper spacing
          const rect = thumbnailImg.getBoundingClientRect();
          const scrollTop = window.pageYOffset + rect.top - 100; // 100px from top of viewport

          window.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });

          if (isDevelopment()) {
            devLog('✅ Scrolled to thumbnail position:', scrollTop);
          }
        } else {
          // No thumbnail - scroll to container with spacing
          const rect = containerElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset + rect.top - 80; // 80px from top of viewport

          window.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });

          if (isDevelopment()) {
            devLog('✅ Scrolled to container position:', scrollTop);
          }
        }
      } else {
        // Fallback: if element not found, try to scroll to a reasonable position
        console.warn('⚠️ video-info-display element not found, using fallback scroll');
        window.scrollTo({ top: 400, behavior: 'smooth' });
      }
    }, 500); // Keep the 500ms delay to ensure everything is rendered

    return () => clearTimeout(timer);
  }, []); // Run once when component mounts

  const formatDuration = (seconds: number): string => {
    if (!seconds) return 'Unknown';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const handleFormatClick = (format: VideoFormat) => {
    const filename = generateFilenameStrict(
      videoInfo.title || 'video',
      sourceUrl,
      format.resolution,
      format.tbr || format.vbr || format.abr,
      format.ext
    );

    onFormatSelect(format, filename);
  };

  const formatDynamicLabel = (format: VideoFormat): string => {
    const { label, ext, is_audio_only, tbr, abr, vbr, filesize, resolution } = format;

    // Always create a dynamic label for audio-only formats, ignoring the server-provided one.
    if (is_audio_only) {
      const formatExt = ext ? `(${ext.toUpperCase()}` : '(';
      const bitrate = tbr || abr || vbr;
      const bitrateText = bitrate ? `, ${Math.round(bitrate)} kbps` : '';
      const filesizeText = filesize ? `, ${(filesize / (1024 * 1024)).toFixed(2)}MB` : '';
      return `Audio Only ${formatExt}${bitrateText}${filesizeText})`;
    }

    if (label) {
      return label; // Return existing label for video formats
    }
    
    // Fallback for video formats without a label
    return `${resolution || 'Video'} (${ext.toUpperCase()})`;
  };

  const copyShareableLink = async () => {
    const shareableUrl = `${window.location.origin}?url=${encodeURIComponent(sourceUrl)}`;

    try {
      await navigator.clipboard.writeText(shareableUrl);
      // You could add a toast notification here
      devLog('Shareable link copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const triggerFormatDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.target as HTMLButtonElement;
    const downloadUrl = button.dataset.downloadUrl;
    const filename = button.dataset.filename;

    if (!downloadUrl || !filename) {
      console.error('Missing download URL or filename');
      return;
    }

    // Only trigger direct download for direct media URLs, not internal API URLs or CDN URLs that need preview page
    if (downloadUrl.includes('/api/download/start-merge') ||
        downloadUrl.includes('/api/tiktok_proxy') ||
        downloadUrl.includes('xhscdn.com') ||
        downloadUrl.includes('xyzcdn.net') ||
        downloadUrl.includes('podcasts.apple.com') ||
        downloadUrl.includes('itunes.apple.com') ||
        downloadUrl.includes('apple.com') ||
        downloadUrl.includes('fireside.fm') ||
        downloadUrl.includes('googlevideo.com') ||
        downloadUrl.includes('youtube.com') ||
        downloadUrl.includes('youtu.be') ||
        downloadUrl.includes('simplecast.com') ||
        downloadUrl.includes('simplecastaudio.com') ||
        downloadUrl.includes('anchor.fm') ||
        downloadUrl.includes('buzzsprout.com') ||
        downloadUrl.includes('audioboom.com') ||
        downloadUrl.includes('podbean.com') ||
        downloadUrl.includes('stitcher.com') ||
        downloadUrl.includes('soundcloud.com') ||
        downloadUrl.includes('snd.sc') ||
        downloadUrl.includes('spotify.com') ||
        downloadUrl.includes('spoti.fi') ||
        downloadUrl.includes('libsyn.com') ||
        downloadUrl.includes('spreaker.com') ||
        downloadUrl.includes('podomatic.com') ||
        downloadUrl.includes('megaphone.fm') ||
        downloadUrl.includes('transistor.fm') ||
        downloadUrl.includes('acast.com') ||
        downloadUrl.includes('castbox.fm') ||
        downloadUrl.includes('player.fm') ||
        downloadUrl.includes('tunein.com') ||
        downloadUrl.includes('overcast.fm') ||
        downloadUrl.includes('pocketcasts.com') ||
        downloadUrl.includes('radiopublic.com') ||
        downloadUrl.includes('breaker.audio') ||
        downloadUrl.includes('iheart.com') ||
        downloadUrl.includes('iheartradio.com')) {
      devLog('Skipping direct download for internal API URL or CDN that needs preview page - will be handled by preview page');
      return;
    }

    // Extract format and platform info for analytics
    const format = button.textContent?.match(/\((\w+),/)?.[1] || 'unknown';
    const platform = videoInfo.platform || 'unknown';
    const videoTitle = videoInfo.title || 'Unknown Video';

    // Track the download event
    trackVideoDownload(platform, videoTitle, format);
    trackClarityVideoDownload(platform, videoTitle, format);

    // Create download link and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success message
    devLog(`✅ Download started: ${filename}`);
  };


  return (
    <div
      id="video-info-display"
      className={`relative bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-left ${className}`}
    >
      {onClear && (
        <button
          onClick={onClear}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Clear results"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
      )}

      {/* Thumbnail */}
      {videoInfo.thumbnail_url && (
        <div className="mb-6 flex justify-center">
          <img
            src={videoInfo.thumbnail_url}
            alt={videoInfo.title || (videoInfo.is_podcast ? 'Podcast episode artwork' : 'Video thumbnail')}
            className="w-full max-w-md rounded-lg shadow-md"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Title */}
      <h3 className="text-xl font-semibold text-gray-800 mb-2">
        {(() => {
          const baseTitle = videoInfo.title || (videoInfo.is_podcast ? 'Untitled Episode' : 'Untitled Video');
          
          // Get platform name from videoInfo.platform or sourceUrl
          let platformName = videoInfo.platform;
          if (!platformName) {
            // Fallback to detecting platform from sourceUrl
            if (sourceUrl.includes('youtube.com') || sourceUrl.includes('youtu.be')) platformName = 'YouTube';
            else if (sourceUrl.includes('tiktok.com')) platformName = 'TikTok';
            else if (sourceUrl.includes('twitter.com') || sourceUrl.includes('x.com')) platformName = 'Twitter';
            else if (sourceUrl.includes('facebook.com')) platformName = 'Facebook';
            else if (sourceUrl.includes('vimeo.com')) platformName = 'Vimeo';
            else if (sourceUrl.includes('bilibili.com')) platformName = 'Bilibili';
            else if (sourceUrl.includes('xiaohongshu.com')) platformName = 'Xiaohongshu';
            else if (sourceUrl.includes('soundcloud.com') || sourceUrl.includes('snd.sc')) platformName = 'SoundCloud';
            else if (sourceUrl.includes('instagram.com')) platformName = 'Instagram';
            else if (sourceUrl.includes('podcasts.apple.com') || sourceUrl.includes('itunes.apple.com')) platformName = 'Apple Podcasts';
            else if (sourceUrl.includes('player.fm')) platformName = 'Player.FM';
            else if (sourceUrl.includes('tunein.com')) platformName = 'TuneIn';
            else if (sourceUrl.includes('castbox.fm')) platformName = 'Castbox';
            else if (sourceUrl.includes('spotify.com') || sourceUrl.includes('spoti.fi')) platformName = 'Spotify';
            else if (sourceUrl.includes('xiaoyuzhoufm.com') || sourceUrl.includes('xyzcdn.net')) platformName = 'Xiaoyuzhou';
            else platformName = 'Unknown Platform';
          }
          
          return `${platformName} | ${baseTitle}`;
        })()}
      </h3>

      {/* Podcast-specific metadata */}
      {videoInfo.is_podcast && (
        <>
          {videoInfo.series && (
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">Podcast:</span> {videoInfo.series}
            </p>
          )}
          {(videoInfo.episode_number || videoInfo.season_number) && (
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">
                {videoInfo.season_number && `Season ${videoInfo.season_number}`}
                {videoInfo.season_number && videoInfo.episode_number && ', '}
                {videoInfo.episode_number && `Episode ${videoInfo.episode_number}`}
              </span>
            </p>
          )}
          {videoInfo.duration && (
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">Duration:</span> {formatDuration(videoInfo.duration)}
            </p>
          )}
        </>
      )}

      {/* Uploader */}
      {videoInfo.uploader && (
        <p className="text-sm text-gray-600 mb-1">
          <span className="font-medium">
            {videoInfo.is_podcast ? 'Creator:' : 'Uploader:'}
          </span> {videoInfo.uploader}
        </p>
      )}

      {/* Description */}
      {videoInfo.description && (
        <p className="text-sm text-gray-600 mb-4">
          {videoInfo.description.length > 150
            ? `${videoInfo.description.substring(0, 150)}...`
            : videoInfo.description}
        </p>
      )}

      {/* Sharing Section */}
      <div
        className="border rounded-lg p-4 mb-4"
        style={{
          backgroundColor: config.branding.colors.surface,
          borderColor: config.branding.colors.primary + '33' // 20% opacity
        }}
      >
        <h4 className="text-sm font-semibold mb-2" style={{ color: config.branding.colors.primary }}>
          {videoInfo.is_podcast ? '🎧 Share This Podcast Download' : '🔗 Share This Download'}
        </h4>
        <p className="text-xs text-gray-600 mb-3">Share this pre-filled download link with others:</p>

        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={`${window.location.origin}?url=${encodeURIComponent(sourceUrl)}`}
            readOnly
            title="Shareable download link"
            className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded bg-white font-mono"
          />
          <button
            onClick={copyShareableLink}
            className="px-3 py-2 text-xs font-medium text-white rounded hover:opacity-90 transition-colors"
            style={{ backgroundColor: config.branding.colors.primary }}
          >
            <span className="fas fa-copy mr-1" />
            Copy
          </button>
        </div>
      </div>

      {/* Podcast Notice */}
      {videoInfo.is_podcast && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-purple-800 mb-2">🎧 Podcast Download Notice</h4>
          <p className="text-xs text-purple-700">
            This appears to be podcast content. Audio-only formats are prioritized. Some podcast platforms may have DRM protection that prevents downloading.
          </p>
        </div>
      )}

      {/* Formats Section */}
      <h4 className="text-lg font-medium text-gray-700 mt-4 mb-3">
        {videoInfo.is_podcast ? 'Available Audio Formats:' : 'Available Formats:'}
      </h4>

      <div className="flex flex-wrap gap-2">
        {videoInfo.formats.map((format, index) => {
          const isAudioOnly = format.is_audio_only;
          const hasAudio = format.has_audio;

          // Choose appropriate icon and styling
          let iconClass = '';
          let buttonClass = '';

          if (isAudioOnly) {
            iconClass = 'fas fa-headphones-alt';
            buttonClass = 'bg-purple-500 hover:bg-purple-600 text-white';
          } else if (hasAudio) {
            iconClass = 'fas fa-volume-up';
            buttonClass = 'bg-green-500 hover:bg-green-600 text-white';
          } else {
            iconClass = 'fas fa-volume-mute text-red-300';
            buttonClass = 'bg-green-500 hover:bg-green-600 text-white';
          }

          return (
            <button
              key={index}
              onClick={(event) => {
                handleFormatClick(format);
                triggerFormatDownload(event);
              }}
              data-download-url={format.url}
              data-filename={generateFilenameStrict(
                videoInfo.title || 'video',
                sourceUrl,
                format.resolution,
                format.tbr || format.vbr || format.abr,
                format.ext
              )}
              className={`${buttonClass} font-medium py-2 px-4 rounded-lg text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-opacity-75 flex items-center`}
            >
              <span className={`${iconClass} mr-2`} />
              {formatDynamicLabel(format)}
            </button>
          );
        })}
      </div>

      {/* Format Info */}
      <div className="mt-4 text-xs text-gray-500">
        <p>💡 Click any format button to open the download preview page</p>
        {videoInfo.is_podcast && (
          <p className="mt-1">🎵 Audio formats are optimized for podcast content</p>
        )}
      </div>
    </div>
  );
};

export default VideoInfoDisplay;
